// External Dependencies
import CardActions from '@mui/material/CardActions';
import { Form, Formik } from 'formik';
import { ImportStatuses } from '@presto-assistant/api_types';
import { toTitleCase } from '@presto-assistant/api_types/utils/toTitleCase';
import { useCallback } from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import type {
  GetOneImportResponse,
  UpdateImportRequest,
} from '@presto-assistant/api_types/api/admin/import';

// Internal Dependencies
import { CustomSelect, SaveButton } from 'components/shared';
import { mapEnum } from 'utils/lib/mapEnum';
import { useUpdateImport } from 'hooks/api/imports';

// Local Typings
interface Props {
  importData: GetOneImportResponse['data'];
  isOpen: boolean;
  onClose: () => void;
}

// Local Variables
const importStatusOptions = mapEnum(ImportStatuses).map((status) => ({
  ...status,
  label: toTitleCase(status.label),
}));

// Component Definition
const ChangeStatusFormDialog = ({
  importData,
  isOpen,
  onClose,
}: Props): JSX.Element => {
  const initialValues: UpdateImportRequest['body'] = {
    importStatusId: importData.import.importStatusId,
  };

  const {
    mutate: updateImport,
  } = useUpdateImport();

  const handleFormikSubmit = useCallback((values: UpdateImportRequest['body']) => {
    // The API expects the importStatusId to be a number
    const importStatusId = Number(values.importStatusId);

    updateImport({
      body: {
        importStatusId,
      },
      params: {
        importId: importData.import.id,
      },
    }, {
      onSuccess: onClose,
    });
  }, []);

  return (
    <Dialog
      fullWidth
      maxWidth="xs"
      onClose={onClose}
      open={isOpen}
    >
      <DialogTitle>
        Change Import Status
      </DialogTitle>

      <Formik<UpdateImportRequest['body']>
        initialValues={initialValues}
        onSubmit={handleFormikSubmit}
      >
        {() => (
          <Form>
            <DialogContent>
              <DialogContentText gutterBottom>
                Updating the status will also assign the import to you.
              </DialogContentText>

              <CustomSelect
                label="Status"
                name="importStatusId"
                options={importStatusOptions}
              />
            </DialogContent>

            <CardActions
              sx={{
                justifyContent: 'flex-end',
              }}
            >
              <Button
                onClick={onClose}
              >
                Cancel
              </Button>

              <SaveButton>
                Update Status
              </SaveButton>
            </CardActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

export default ChangeStatusFormDialog;
